import type { ForwardedRef } from 'react';

import { useEffect, useImperativeHandle, useLayoutEffect, useMemo } from 'react';
import { ANIMATION_DELAY } from '@ricoh/ria.client.core/constants/ui/animation';
import { useLoadingScreen } from '@ricoh/ria.client.core/hooks/component/loading';
import { componentLayoutSliceActions } from '@ricoh/ria.client.core/plugins/store/slices/component/layout';

import { useStoreDispatch } from '~/plugins/store/hooks/dispatch';

import type { AppRouterProviderHooks, AppRouterProviderProps, AppRouterProviderRef } from './_.type';

/**
 * Use AppRouterProvider hooks.
 *
 * @param {AppRouterProviderProps} props AppRouterProvider props.
 * @param {AppRouterProviderRef} ref AppRouterProvider ref.
 *
 * @returns {AppRouterProviderHooks} AppRouterProvider hooks.
 */
export default function useAppRouterProviderHooks(
  { reloadDocument = false }: AppRouterProviderProps,
  ref: ForwardedRef<AppRouterProviderRef>,
): AppRouterProviderHooks {
  const dispatch = useStoreDispatch();

  const loadingScreen = useLoadingScreen();

  useLayoutEffect(() => {
    dispatch(componentLayoutSliceActions.router_setReloadDocument(reloadDocument));
  }, [dispatch, reloadDocument]);

  useEffect(() => {
    loadingScreen.hide(ANIMATION_DELAY.FAST);

    return () => {
      loadingScreen.show(1);
    };
  }, [loadingScreen]);

  useImperativeHandle(ref, () => ({}));

  return useMemo<AppRouterProviderHooks>(
    () => ({
      refs: {},

      states: {},

      events: {},
    }),
    [],
  );
}
